import React, { useState, useEffect, useRef } from "react";
import {
  FaArrowLeft,
  FaCreditCard,
  FaPaypal,
  FaBuilding,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaShieldAlt,
  FaCheckCircle,
  FaCcVisa,
  FaCcMastercard,
  FaCcAmex,
  FaCcDiscover,
  FaRegCreditCard,
  FaRegBuilding,
  FaMicrochip,
  FaRss,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

function OnlinePaymentPage() {
  const navigate = useNavigate();
  const { cardId } = useParams();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);

  const [selectedCard, setSelectedCard] = useState(() => {
    const saved = localStorage.getItem('selected_card');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  const [paymentMethod, setPaymentMethod] = useState("card");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    companyName: "",
    taxId: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "Pakistan",
    cardNumber: "",
    cardHolder: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
    bankAccountName: "",
    bankAccountNumber: "",
    bankName: "",
    paypalEmail: "",
  });

  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [touched, setTouched] = useState({});
  const [showCVV, setShowCVV] = useState(false);

  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('header') ||
                    document.querySelector('.header') ||
                    document.querySelector('#header');

      if (header) {
        setHeaderHeight(header.offsetHeight);
      } else {
        if (windowWidth < 640) {
          setHeaderHeight(80);
        } else if (windowWidth < 768) {
          setHeaderHeight(96);
        } else if (windowWidth < 1024) {
          setHeaderHeight(112);
        } else {
          setHeaderHeight(120);
        }
      }
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      updateHeaderHeight();
    };

    updateHeaderHeight();
    window.addEventListener("resize", handleResize);

    const observer = new ResizeObserver(updateHeaderHeight);
    const headerElement = headerRef.current || document.querySelector('header');
    if (headerElement) observer.observe(headerElement);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (headerElement) observer.unobserve(headerElement);
    };
  }, [windowWidth]);

  const getCardBrand = (cardNumber) => {
    const cleaned = cardNumber.replace(/\s/g, "");
    if (cleaned.startsWith("4")) return "visa";
    if (cleaned.startsWith("5")) return "mastercard";
    if (cleaned.startsWith("3")) return "amex";
    if (cleaned.startsWith("6")) return "discover";
    return "unknown";
  };

  const getCardGradient = () => {
    const brand = getCardBrand(formData.cardNumber);
    switch(brand) {
      case "visa":
        return "bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900";
      case "mastercard":
        return "bg-gradient-to-br from-red-600 via-orange-600 to-yellow-800";
      case "amex":
        return "bg-gradient-to-br from-blue-500 via-cyan-600 to-teal-800";
      case "discover":
        return "bg-gradient-to-br from-orange-500 via-orange-600 to-amber-800";
      default:
        return "bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900";
    }
  };

  const getCardLogo = () => {
    const brand = getCardBrand(formData.cardNumber);
    switch(brand) {
      case "visa":
        return <FaCcVisa className="text-white text-5xl opacity-90" />;
      case "mastercard":
        return (
          <div className="flex -space-x-2">
            <div className="w-10 h-10 bg-red-500 rounded-full opacity-90"></div>
            <div className="w-10 h-10 bg-yellow-500 rounded-full opacity-90"></div>
          </div>
        );
      case "amex":
        return <FaCcAmex className="text-white text-5xl opacity-90" />;
      case "discover":
        return <FaCcDiscover className="text-white text-5xl opacity-90" />;
      default:
        return <FaRegCreditCard className="text-white text-5xl opacity-70" />;
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case "fullName":
        if (!value.trim()) return "Full name is required";
        if (value.trim().length < 3) return "Name must be at least 3 characters";
        return "";
      case "email":
        if (!value.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email format";
        return "";
      case "phone":
        if (!value.trim()) return "Phone number is required";
        if (!/^[\d\s\+\-\(\)]{8,}$/.test(value)) return "Invalid phone number";
        return "";
      case "companyName":
        if (!value.trim()) return "Company name is required";
        return "";
      case "address":
        if (!value.trim()) return "Address is required";
        return "";
      case "city":
        if (!value.trim()) return "City is required";
        return "";
      case "state":
        if (!value.trim()) return "State/Province is required";
        return "";
      case "zipCode":
        if (!value.trim()) return "ZIP/Postal code is required";
        return "";
      case "cardNumber":
        if (paymentMethod === "card") {
          if (!value.trim()) return "Card number is required";
          const cleaned = value.replace(/\s/g, "");
          const brand = getCardBrand(cleaned);
          if (brand === "amex") {
            if (!/^\d{15}$/.test(cleaned)) return "Amex requires 15 digits";
          } else if (brand === "discover") {
            if (!/^\d{16}$/.test(cleaned)) return "Discover requires 16 digits";
          } else {
            if (!/^\d{16}$/.test(cleaned)) return "Invalid card number (16 digits)";
          }
          return "";
        }
        return "";
      case "expiryMonth":
        if (paymentMethod === "card") {
          if (!value) return "Expiry month is required";
          const month = parseInt(value);
          if (month < 1 || month > 12) return "Invalid month";
          return "";
        }
        return "";
      case "expiryYear":
        if (paymentMethod === "card") {
          if (!value) return "Expiry year is required";
          const year = parseInt(value);
          const currentYear = new Date().getFullYear();
          if (year < currentYear || year > currentYear + 10) return "Invalid year";
          return "";
        }
        return "";
      case "cvv":
        if (paymentMethod === "card") {
          if (!value.trim()) return "CVV is required";
          const cleaned = formData.cardNumber.replace(/\s/g, "");
          const brand = getCardBrand(cleaned);
          if (brand === "amex") {
            if (!/^\d{4}$/.test(value.trim())) return "Amex CVV is 4 digits";
          } else {
            if (!/^\d{3}$/.test(value.trim())) return "CVV must be 3 digits";
          }
          return "";
        }
        return "";
      case "bankAccountName":
        if (paymentMethod === "bank") {
          if (!value.trim()) return "Account holder name is required";
          return "";
        }
        return "";
      case "bankAccountNumber":
        if (paymentMethod === "bank") {
          if (!value.trim()) return "Account number is required";
          return "";
        }
        return "";
      case "bankName":
        if (paymentMethod === "bank") {
          if (!value.trim()) return "Bank name is required";
          return "";
        }
        return "";
      case "paypalEmail":
        if (paymentMethod === "paypal") {
          if (!value.trim()) return "PayPal email is required";
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email format";
          return "";
        }
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === "cardNumber") {
      processedValue = value
        .replace(/\s/g, "")
        .slice(0, 16)
        .replace(/(\d{4})/g, "$1 ")
        .trim();
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }));

    if (touched[name]) {
      const error = validateField(name, processedValue);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const fieldsToValidate = [
      "fullName", "email", "phone", "companyName",
      "address", "city", "state", "zipCode"
    ];

    if (paymentMethod === "card") {
      fieldsToValidate.push("cardNumber", "expiryMonth", "expiryYear", "cvv");
    } else if (paymentMethod === "bank") {
      fieldsToValidate.push("bankAccountName", "bankAccountNumber", "bankName");
    } else if (paymentMethod === "paypal") {
      fieldsToValidate.push("paypalEmail");
    }

    const newErrors = {};
    let isValid = true;

    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setIsProcessing(true);

    setTimeout(() => {
      setIsProcessing(false);
      setPaymentSuccess(true);

      setTimeout(() => {
        navigate("/payment-success", {
          state: {
            cardDetails: selectedCard,
            transactionId: "TXN" + Math.random().toString(36).substr(2, 10).toUpperCase()
          }
        });
      }, 2000);
    }, 2000);
  };

  if (!selectedCard) {
    return (
      <div
        className="w-full min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center"
        style={{ paddingTop: `${headerHeight}px` }}
      >
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="text-6xl mb-4">💳</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Plan Selected</h2>
            <p className="text-gray-600 mb-6">Please select a premium plan first.</p>
            <button
              onClick={() => navigate("/store-cards")}
              className="px-6 py-3 bg-[#70B444] text-white rounded-xl font-semibold hover:bg-[#5d9138] transition-all duration-300"
            >
              Browse Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div
        className="w-full min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center"
        style={{ paddingTop: `${headerHeight}px` }}
      >
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center animate-in zoom-in duration-300">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaCheckCircle className="text-green-500 text-4xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Initiated!</h2>
            <p className="text-gray-600 mb-4">
              Your payment is being processed. You will receive a confirmation email shortly.
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-500">Plan: <span className="font-semibold text-gray-700">{selectedCard.name}</span></p>
              <p className="text-sm text-gray-500">Amount: <span className="font-semibold text-[#70B444]">PKR {selectedCard.monthly_fee}/month</span></p>
            </div>
            <div className="animate-pulse">
              <p className="text-sm text-gray-400">Redirecting to confirmation...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full min-h-screen bg-gradient-to-b from-gray-50 to-gray-100"
      ref={headerRef}
    >
      <div
        className="w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-8"
        style={{ paddingTop: `${headerHeight + 100}px`, paddingBottom: "60px" }}
      >
        {/* Breadcrumb section - now with more visible spacing */}
        <div className="flex flex-wrap items-center text-gray-500 mb-8 text-sm md:text-base">
          <span
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/")}
          >
            Home
          </span>
          <span className="mx-2">»</span>
          <span
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/store-cards")}
          >
            Premium Store Cards
          </span>
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            Secure Checkout
          </span>
        </div>

        {/* Header Section with increased top margin */}
        <div className="text-center mb-10 mt-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Complete Your <span className="text-[#70B444]">Payment</span>
          </h1>
          <p className="text-gray-500 text-sm">
            Secure checkout for {selectedCard.name} plan
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Payment Form - Left Section */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Realistic Credit Card Display */}
              {paymentMethod === "card" && (
                <div className="p-5 bg-gradient-to-r from-gray-900 to-gray-800">
                  <div className={`relative rounded-2xl p-5 text-white shadow-2xl transition-all duration-300 transform hover:scale-105 ${getCardGradient()}`}>
                    {/* Card Chip */}
                    <div className="absolute top-5 left-5">
                      <div className="w-10 h-8 bg-yellow-300 rounded-md opacity-80 flex items-center justify-center">
                        <FaMicrochip className="text-gray-800 text-xl" />
                      </div>
                    </div>

                    {/* Contactless Icon */}
                    <div className="absolute top-5 right-5">
                      <FaRss className="text-2xl text-white opacity-60 transform rotate-90" />
                    </div>

                    {/* Card Number */}
                    <div className="mt-12 mb-4">
                      <p className="text-xs opacity-70 mb-1 tracking-wider">CARD NUMBER</p>
                      <p className="text-xl tracking-wider font-mono">
                        {formData.cardNumber || "**** **** **** ****"}
                      </p>
                    </div>

                    {/* Card Details Row */}
                    <div className="flex justify-between items-end mb-4">
                      <div>
                        <p className="text-xs opacity-70 mb-1 tracking-wider">CARDHOLDER NAME</p>
                        <p className="text-sm uppercase tracking-wider font-medium">
                          {formData.cardHolder || "YOUR NAME HERE"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs opacity-70 mb-1 tracking-wider">EXPIRY DATE</p>
                        <p className="text-sm font-mono">
                          {formData.expiryMonth || "MM"}/{formData.expiryYear || "YY"}
                        </p>
                      </div>
                    </div>

                    {/* Card Brand Logo */}
                    <div className="absolute bottom-5 right-5">
                      {getCardLogo()}
                    </div>

                    {/* Background Pattern */}
                    <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                      <div className="absolute -top-20 -right-20 w-40 h-40 bg-white opacity-10 rounded-full"></div>
                      <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-white opacity-10 rounded-full"></div>
                    </div>
                  </div>

                  {/* CVV Hint */}
                  <div className="mt-3 text-center">
                    <button
                      type="button"
                      onMouseEnter={() => setShowCVV(true)}
                      onMouseLeave={() => setShowCVV(false)}
                      className="text-xs text-gray-500 hover:text-[#70B444] transition-colors"
                    >
                      {showCVV ? "CVV is the 3-digit code on the back of your card" : "❓ Where is my CVV?"}
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="p-5 md:p-6">
                {/* Account Information Section */}
                <div className="mb-6">
                  <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FaUser className="text-[#70B444] text-sm" />
                    Account Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none transition-all ${
                          errors.fullName && touched.fullName ? "border-red-500 bg-red-50" : "border-gray-300"
                        }`}
                        placeholder="John Doe"
                      />
                      {errors.fullName && touched.fullName && (
                        <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none transition-all ${
                            errors.email && touched.email ? "border-red-500 bg-red-50" : "border-gray-300"
                          }`}
                          placeholder="john@company.com"
                        />
                      </div>
                      {errors.email && touched.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none transition-all ${
                            errors.phone && touched.phone ? "border-red-500 bg-red-50" : "border-gray-300"
                          }`}
                          placeholder="+92 300 1234567"
                        />
                      </div>
                      {errors.phone && touched.phone && (
                        <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Company Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <FaRegBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none transition-all ${
                            errors.companyName && touched.companyName ? "border-red-500 bg-red-50" : "border-gray-300"
                          }`}
                          placeholder="Dental Lab Inc."
                        />
                      </div>
                      {errors.companyName && touched.companyName && (
                        <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Tax ID (optional)
                      </label>
                      <input
                        type="text"
                        name="taxId"
                        value={formData.taxId}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none transition-all"
                        placeholder="NTN / GST"
                      />
                    </div>
                  </div>
                </div>

                {/* Billing Address Section */}
                <div className="mb-6">
                  <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FaBuilding className="text-[#70B444] text-sm" />
                    Billing Address
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Street Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none transition-all ${
                          errors.address && touched.address ? "border-red-500 bg-red-50" : "border-gray-300"
                        }`}
                        placeholder="Suite 123, Main Street"
                      />
                      {errors.address && touched.address && (
                        <p className="text-red-500 text-xs mt-1">{errors.address}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">City <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none ${
                            errors.city && touched.city ? "border-red-500 bg-red-50" : "border-gray-300"
                          }`}
                        />
                        {errors.city && touched.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">State <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none ${
                            errors.state && touched.state ? "border-red-500 bg-red-50" : "border-gray-300"
                          }`}
                        />
                        {errors.state && touched.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">ZIP <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none ${
                            errors.zipCode && touched.zipCode ? "border-red-500 bg-red-50" : "border-gray-300"
                          }`}
                        />
                        {errors.zipCode && touched.zipCode && <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none"
                      >
                        <option value="Pakistan">Pakistan</option>
                        <option value="UAE">United Arab Emirates</option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="USA">United States</option>
                        <option value="UK">United Kingdom</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="mb-6">
                  <h3 className="text-md font-semibold text-gray-800 mb-3">Payment Method</h3>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {[
                      { id: "card", label: "Credit Card", icon: <FaCreditCard /> },
                      { id: "bank", label: "Bank Transfer", icon: <FaBuilding /> },
                      { id: "paypal", label: "PayPal", icon: <FaPaypal /> },
                    ].map((method) => (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => {
                          setPaymentMethod(method.id);
                          setErrors({});
                        }}
                        className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all text-sm ${
                          paymentMethod === method.id
                            ? "border-[#70B444] bg-[#70B444]/5 text-[#70B444]"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        {method.icon}
                        <span className="text-xs font-medium">{method.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Card Payment Fields */}
                  {paymentMethod === "card" && (
                    <div className="space-y-3 animate-in fade-in duration-200">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Card Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none font-mono ${
                            errors.cardNumber && touched.cardNumber ? "border-red-500 bg-red-50" : "border-gray-300"
                          }`}
                          placeholder="1234 5678 9012 3456"
                          maxLength="19"
                        />
                        {errors.cardNumber && touched.cardNumber && (
                          <p className="text-red-500 text-xs mt-1">{errors.cardNumber}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Cardholder Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="cardHolder"
                          value={formData.cardHolder}
                          onChange={handleChange}
                          className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none uppercase"
                          placeholder="NAME ON CARD"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Expiry Date <span className="text-red-500">*</span>
                          </label>
                          <div className="flex gap-2">
                            <select
                              name="expiryMonth"
                              value={formData.expiryMonth}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`w-1/2 px-2 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] outline-none ${
                                errors.expiryMonth && touched.expiryMonth ? "border-red-500" : "border-gray-300"
                              }`}
                            >
                              <option value="">MM</option>
                              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                <option key={m} value={m.toString().padStart(2, "0")}>{m.toString().padStart(2, "0")}</option>
                              ))}
                            </select>
                            <select
                              name="expiryYear"
                              value={formData.expiryYear}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`w-1/2 px-2 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] outline-none ${
                                errors.expiryYear && touched.expiryYear ? "border-red-500" : "border-gray-300"
                              }`}
                            >
                              <option value="">YYYY</option>
                              {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() + i).map(y => (
                                <option key={y} value={y}>{y}</option>
                              ))}
                            </select>
                          </div>
                          {(errors.expiryMonth || errors.expiryYear) && (
                            <p className="text-red-500 text-xs mt-1">Valid expiry date required</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            CVV <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            name="cvv"
                            value={formData.cvv}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] focus:border-transparent outline-none font-mono ${
                              errors.cvv && touched.cvv ? "border-red-500 bg-red-50" : "border-gray-300"
                            }`}
                            placeholder="123"
                            maxLength="4"
                          />
                          {errors.cvv && touched.cvv && <p className="text-red-500 text-xs mt-1">{errors.cvv}</p>}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bank Transfer Fields */}
                  {paymentMethod === "bank" && (
                    <div className="space-y-3 animate-in fade-in duration-200">
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                        <p className="text-xs text-blue-800 flex items-start gap-2">
                          <FaShieldAlt className="text-blue-600 mt-0.5 text-xs" />
                          <span>Bank transfers processed within 1-2 business days.</span>
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Account Holder Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="bankAccountName"
                          value={formData.bankAccountName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] outline-none ${
                            errors.bankAccountName && touched.bankAccountName ? "border-red-500 bg-red-50" : "border-gray-300"
                          }`}
                        />
                        {errors.bankAccountName && touched.bankAccountName && (
                          <p className="text-red-500 text-xs mt-1">{errors.bankAccountName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Account Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="bankAccountNumber"
                          value={formData.bankAccountNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] outline-none ${
                            errors.bankAccountNumber && touched.bankAccountNumber ? "border-red-500 bg-red-50" : "border-gray-300"
                          }`}
                        />
                        {errors.bankAccountNumber && touched.bankAccountNumber && (
                          <p className="text-red-500 text-xs mt-1">{errors.bankAccountNumber}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Bank Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="bankName"
                          value={formData.bankName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] outline-none ${
                            errors.bankName && touched.bankName ? "border-red-500 bg-red-50" : "border-gray-300"
                          }`}
                        />
                        {errors.bankName && touched.bankName && (
                          <p className="text-red-500 text-xs mt-1">{errors.bankName}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* PayPal Fields */}
                  {paymentMethod === "paypal" && (
                    <div className="space-y-3 animate-in fade-in duration-200">
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                        <p className="text-xs text-blue-800">You will be redirected to PayPal to complete your payment securely.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          PayPal Email <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FaPaypal className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-sm" />
                          <input
                            type="email"
                            name="paypalEmail"
                            value={formData.paypalEmail}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#70B444] outline-none ${
                              errors.paypalEmail && touched.paypalEmail ? "border-red-500 bg-red-50" : "border-gray-300"
                            }`}
                            placeholder="your-email@paypal.com"
                          />
                        </div>
                        {errors.paypalEmail && touched.paypalEmail && (
                          <p className="text-red-500 text-xs mt-1">{errors.paypalEmail}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isProcessing}
                  className={`w-full py-3 rounded-xl font-semibold text-white transition-all duration-300 flex items-center justify-center gap-2 text-sm ${
                    isProcessing ? "bg-gray-400 cursor-not-allowed" : "bg-[#70B444] hover:bg-[#5d9138] hover:shadow-lg transform hover:-translate-y-0.5"
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaLock className="text-xs" />
                      Pay PKR {selectedCard.monthly_fee || "0"}/month
                    </>
                  )}
                </button>

                {/* Security Notice */}
                <div className="mt-3 text-center flex items-center justify-center gap-2 text-xs text-gray-400">
                  <FaLock className="text-gray-400 text-xs" />
                  <span>Secure & encrypted payment</span>
                </div>
              </form>
            </div>
          </div>

          {/* Order Summary - Right Section with Colored Card Icons */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-5 sticky top-24">
              <h3 className="text-md font-bold text-gray-800 mb-3">Order Summary</h3>

              <div className={`rounded-xl p-3 mb-4 ${selectedCard.name?.toLowerCase().includes("premium") ? "bg-gradient-to-r from-[#70B444]/10 to-[#538630]/10 border-l-3 border-[#70B444]" : "bg-gray-50"}`}>
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{selectedCard.name || "Premium Plan"}</p>
                    <p className="text-xs text-gray-500">Billed monthly</p>
                  </div>
                  <p className="font-bold text-[#70B444] text-sm">
                    PKR {selectedCard.monthly_fee || "0"}
                    <span className="text-xs text-gray-400 font-normal">/mo</span>
                  </p>
                </div>
              </div>

              <div className="space-y-2 border-b border-gray-100 pb-3 mb-3">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-800">PKR {selectedCard.monthly_fee || "0"}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Tax (GST)</span>
                  <span className="text-gray-800">PKR 0</span>
                </div>
              </div>

              <div className="flex justify-between mb-4">
                <span className="font-bold text-gray-900 text-sm">Total Due Today</span>
                <span className="font-bold text-lg text-[#70B444]">PKR {selectedCard.monthly_fee || "0"}</span>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <FaCheckCircle className="text-[#70B444] text-xs" />
                  <span>Secure SSL Encryption</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <FaCheckCircle className="text-[#70B444] text-xs" />
                  <span>24/7 Customer Support</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <FaCheckCircle className="text-[#70B444] text-xs" />
                  <span>30-Day Money Back Guarantee</span>
                </div>
              </div>

              {/* Colored Card Icons Section */}
              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500 text-center mb-2">We Accept</p>
                <div className="flex justify-center items-center gap-3">
                  <div className="bg-white rounded-lg p-1.5 shadow-sm">
                    <FaCcVisa className="text-3xl text-blue-700" />
                  </div>
                  <div className="bg-white rounded-lg p-1.5 shadow-sm">
                    <FaCcMastercard className="text-3xl text-orange-600" />
                  </div>
                  <div className="bg-white rounded-lg p-1.5 shadow-sm">
                    <FaCcAmex className="text-3xl text-blue-600" />
                  </div>
                  <div className="bg-white rounded-lg p-1.5 shadow-sm">
                    <FaCcDiscover className="text-3xl text-orange-500" />
                  </div>
                  <div className="bg-white rounded-lg p-1.5 shadow-sm">
                    <FaPaypal className="text-3xl text-blue-600" />
                  </div>
                </div>
                <p className="text-center text-xs text-gray-400 mt-3">
                  All major credit and debit cards accepted
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnlinePaymentPage;